/*
 * <sticky-atc> — native sticky add-to-cart bar for product pages.
 *
 * Native replacement for the Visually.io sticky cart. Server-rendered by
 * sections/sticky-atc.liquid (real product data, no external API). Behaviour:
 *   - Appears after the shopper scrolls past `data-threshold`, hides while the
 *     cart drawer is open.
 *   - A variant <select> (multi-variant products) updates the shown price,
 *     thumbnail, and button state, and is the variant added to the cart.
 *   - On mobile the image and dropdown are hidden. Add to cart opens a
 *     popup of the same choices, and the picked choice is what gets added.
 *   - Add to cart uses /cart/add.js and renders into the theme's cart drawer /
 *     notification (same mechanism Dawn's product form uses), falling back to
 *     the cart page.
 *   - Bundle pages: if a <bundle-builder> is present, its options are mirrored
 *     into the dropdown and Add to Cart is delegated to it (it adds the correct
 *     multi-line bundle and the Cart Transform re-prices it).
 */
(function () {
  if (window.customElements && customElements.get('sticky-atc')) return;

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.threshold, 10) || 300;
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.designMode = !!(window.Shopify && window.Shopify.designMode);

      this.select = this.querySelector('[data-sticky-variant]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.imageEl = this.querySelector('.sticky-atc__image');
      this.button = this.querySelector('[data-sticky-add]');
      this.defaultLabel = this.button ? this.button.textContent.trim() : 'Add to cart';

      this.cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');

      this.bundleWired = false;
      this.sheet = document.querySelector('[data-sticky-sheet]');

      this.onScroll = this.updateVisibility.bind(this);
      this.onAdd = () => this.handleAdd(false);

      if (this.sheet) {
        this.sheet.addEventListener('click', (event) => {
          if (event.target.closest('[data-sticky-sheet-close]')) this.closeSheet();
        });
      }

      if (this.select) {
        this.select.addEventListener('change', () => {
          if (this.bundle) {
            // Bundle page: reflect the choice onto the on-page bundle card live.
            this.syncBundleSelection();
            this.updateBundleState();
          } else {
            // Variant page: drive the PDP (price / media / URL) so the bar and
            // the page stay in lock-step, then refresh the bar itself.
            this.syncVariantToPage();
            this.onVariantChange();
          }
        });
      }
      if (this.button) this.button.addEventListener('click', this.onAdd);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      // Catch drawer open/close (Dawn toggles body.overflow-hidden + drawer.active).
      this.drawerObserver = new MutationObserver(this.onScroll);
      this.drawerObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      // The <bundle-builder> can mount after this script runs; poll briefly.
      this.watchBundle();

      // Reflect on-page variant changes (Dawn pub/sub) into the sticky bar.
      this.subscribeVariantChange();

      this.removeAttribute('hidden');
      this.onVariantChange();
      this.updateVisibility();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.drawerObserver) this.drawerObserver.disconnect();
      if (this.unsubscribeVariant) this.unsubscribeVariant();
    }

    /* ---------------- Visibility ---------------- */
    isDrawerOpen() {
      if (document.body.classList.contains('overflow-hidden')) return true;
      return !!document.querySelector(
        'cart-drawer.active, .cart-drawer.active, cart-drawer.animate, #CartDrawer.active'
      );
    }

    updateVisibility() {
      if (this.designMode) {
        this.classList.add('is-visible');
        return;
      }
      const show = window.scrollY > this.threshold && !this.isDrawerOpen();
      this.classList.toggle('is-visible', show);
      this.classList.toggle('is-suppressed', this.isDrawerOpen());
    }

    /* ---------------- Normal variant mode ---------------- */
    selectedOption() {
      if (!this.select) return null;
      return this.select.options[this.select.selectedIndex] || null;
    }

    onVariantChange() {
      if (this.bundle) {
        this.updateBundleState();
        return;
      }
      const opt = this.selectedOption();
      if (opt) {
        if (this.priceEl && opt.dataset.price) this.priceEl.textContent = opt.dataset.price;
        const available = opt.dataset.available !== 'false';
        this.setButton(available);
        this.updateImage(opt);
      }
    }

    updateImage(opt) {
      const img = this.imageEl || this.querySelector('.sticky-atc__image');
      if (!img) return;

      let url = opt && opt.dataset.image;
      let srcset = opt && opt.dataset.imageSrcset;

      if (!url) {
        const galleryImg = document.querySelector(
          'media-gallery .product__media-item.is-active img, .product__media-item.is-active img'
        );
        if (galleryImg) {
          url = galleryImg.currentSrc || galleryImg.src;
          srcset = galleryImg.getAttribute('srcset') || '';
        }
      }
      if (!url) return;

      img.src = url;
      if (srcset) img.srcset = srcset;
      else img.removeAttribute('srcset');
    }

    setButton(available, label) {
      if (!this.button) return;
      const choices = this.isMobile() ? this.getSheetChoices() : [];
      const canChoose = choices.length > 1 && choices.some((choice) => choice.available);
      const enabled = available || canChoose;
      this.button.disabled = !enabled;
      this.button.textContent =
        label || (enabled ? this.defaultLabel : (window.variantStrings && window.variantStrings.soldOut) || 'Sold out');
    }

    setLoading(loading) {
      if (!this.button) return;
      this.button.classList.toggle('loading', loading);
      this.button.disabled = loading;
    }

    /* ---------------- Bundle mode ---------------- */
    getBundleBuilder() {
      return document.querySelector('bundle-builder');
    }

    watchBundle() {
      const existing = this.getBundleBuilder();
      if (existing) {
        this.enableBundleMode(existing);
        return;
      }
      let tries = 0;
      const timer = setInterval(() => {
        const bb = this.getBundleBuilder();
        if (bb) {
          this.enableBundleMode(bb);
          clearInterval(timer);
        } else if (++tries >= 20) {
          clearInterval(timer);
        }
      }, 300);
    }

    bundleOptions(bb) {
      const inputs = Array.prototype.slice.call(bb.querySelectorAll('.bundle-card__input'));
      return inputs.map((inp) => {
        const label = bb.querySelector('label[for="' + inp.id + '"]');
        const titleEl = label && label.querySelector('.bundle-card__title');
        const soldout = inp.disabled || !!(label && label.classList.contains('bundle-card--soldout'));
        return {
          value: inp.value,
          title: titleEl ? titleEl.textContent.trim() : inp.value,
          soldout: soldout,
          input: inp,
        };
      });
    }

    enableBundleMode(bb) {
      this.bundle = bb;
      if (!this.select) return;
      const opts = this.bundleOptions(bb);
      if (!opts.length) return;

      this.select.innerHTML = '';
      opts.forEach((o) => {
        const option = document.createElement('option');
        option.value = o.value;
        option.textContent = o.soldout ? o.title + ' — Sold out' : o.title;
        this.select.appendChild(option);
      });
      const checked = opts.filter((o) => o.input.checked)[0] || opts[0];
      this.select.value = checked.value;
      this.select.closest('.sticky-atc__select-wrap').hidden = false;

      // Reverse sync: shopper changes the option on the page → reflect here.
      if (!this.bundleWired) {
        this.bundleWired = true;
        opts.forEach((o) => {
          o.input.addEventListener('change', () => {
            if (o.input.checked) {
              this.select.value = o.value;
              this.updateBundleState();
            }
          });
        });
      }

      this.updateBundleState();
    }

    selectedBundleOption() {
      if (!this.bundle) return null;
      const opts = this.bundleOptions(this.bundle);
      if (!opts.length) return null;
      if (this.select && this.select.value) {
        const match = opts.filter((o) => o.value === this.select.value)[0];
        if (match) return match;
      }
      return opts.filter((o) => o.input.checked)[0] || opts[0];
    }

    syncBundleSelection() {
      if (!this.bundle || !this.select) return;
      const sel = this.bundleOptions(this.bundle).filter((o) => o.value === this.select.value)[0];
      if (sel && sel.input && !sel.input.disabled && !sel.input.checked) {
        sel.input.checked = true;
        sel.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateBundleState() {
      const sel = this.selectedBundleOption();
      if (!sel) {
        this.setButton(false, (window.variantStrings && window.variantStrings.unavailable) || 'Unavailable');
      } else if (sel.soldout) {
        this.setButton(false);
      } else {
        this.setButton(true);
      }
      // Mirror the bundle total into the sticky price if it's shown.
      if (this.priceEl && this.bundle) {
        const totalEl = this.bundle.querySelector('.bundle-builder__total');
        const v = this.select ? this.select.value : null;
        if (totalEl && v) {
          const raw =
            v === 'bundle'
              ? totalEl.getAttribute('data-bundle-total')
              : totalEl.getAttribute('data-base-total');
          if (raw) this.priceEl.textContent = raw;
        }
      }
      this.updateImageFromBundle();
    }

    updateImageFromBundle() {
      const img = this.imageEl || this.querySelector('.sticky-atc__image');
      if (!img || !this.bundle) return;
      const sel = this.selectedBundleOption();
      const card = sel && sel.input && sel.input.closest('label');
      const cardImg = card && card.querySelector('img');
      if (!cardImg) return;
      const url = cardImg.currentSrc || cardImg.src;
      if (!url) return;
      img.src = url;
      const srcset = cardImg.getAttribute('srcset');
      if (srcset) img.srcset = srcset;
      else img.removeAttribute('srcset');
    }

    /* ---------------- Mobile variant popup ---------------- */
    isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    getSheetChoices() {
      if (this.bundle) {
        return this.bundleOptions(this.bundle).map((option) => {
          const card = option.input && option.input.closest('label');
          const cardImg = card && card.querySelector('img');
          return {
            kind: 'select',
            value: option.value,
            title: option.title,
            price: '',
            image: cardImg ? cardImg.currentSrc || cardImg.getAttribute('src') || '' : '',
            available: !option.soldout,
          };
        });
      }

      const radios = document.querySelectorAll('.hwv2__radio');
      const selectCount = this.select ? this.select.options.length : 0;
      if (radios.length > 1 && selectCount <= 1) {
        return Array.prototype.slice.call(radios).map((radio) => {
          const card = radio.closest('.hwv2__card');
          const name = card && card.querySelector('.hwv2__name');
          const cardImg = card && card.querySelector('img');
          return {
            kind: 'hwv2',
            value: radio.getAttribute('data-variant-id'),
            title: name ? name.textContent.trim() : radio.value,
            price: radio.getAttribute('data-now') || '',
            image: (cardImg && (cardImg.currentSrc || cardImg.getAttribute('src'))) || radio.getAttribute('data-hero') || '',
            available: !radio.disabled,
            radio: radio,
          };
        });
      }

      if (!this.select) return [];
      return Array.prototype.slice.call(this.select.options).map((option) => ({
        kind: 'select',
        value: option.value,
        title: option.textContent.trim(),
        price: option.dataset.price || '',
        image: option.dataset.image || '',
        available: option.dataset.available !== 'false',
      }));
    }

    shouldOpenSheet() {
      if (!this.isMobile() || !this.sheet) return false;
      return this.getSheetChoices().length > 1;
    }

    openSheet() {
      const list = this.sheet.querySelector('[data-sticky-sheet-list]');
      if (!list) return;
      const choices = this.getSheetChoices();
      list.replaceChildren();

      choices.forEach((choice) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sticky-atc__sheet-option' + (choice.available ? '' : ' is-unavailable');
        button.disabled = !choice.available;

        if (choice.image) {
          const media = document.createElement('span');
          media.className = 'sticky-atc__sheet-media';
          const image = document.createElement('img');
          image.src = choice.image;
          image.alt = '';
          image.width = 72;
          image.height = 72;
          media.appendChild(image);
          button.appendChild(media);
        }

        const name = document.createElement('span');
        name.className = 'sticky-atc__sheet-name';
        name.textContent = choice.title;
        const copy = document.createElement('span');
        copy.className = 'sticky-atc__sheet-copy';
        copy.appendChild(name);
        button.appendChild(copy);

        if (choice.price) {
          const price = document.createElement('span');
          price.className = 'sticky-atc__sheet-price';
          price.textContent = choice.price;
          button.appendChild(price);
        }

        button.addEventListener('click', () => this.chooseSheetOption(choice));
        list.appendChild(button);
      });

      this.sheet.hidden = false;
      document.body.classList.add('sticky-atc-sheet-open');
      this.onSheetKeydown = (event) => {
        if (event.key === 'Escape') this.closeSheet();
      };
      document.addEventListener('keydown', this.onSheetKeydown);

      const first = list.querySelector('button:not([disabled])');
      if (first) first.focus();
    }

    closeSheet() {
      if (!this.sheet || this.sheet.hidden) return;
      this.sheet.hidden = true;
      document.body.classList.remove('sticky-atc-sheet-open');
      if (this.onSheetKeydown) document.removeEventListener('keydown', this.onSheetKeydown);
      if (this.button) this.button.focus();
    }

    chooseSheetOption(choice) {
      if (!choice || !choice.available) return;
      this.closeSheet();

      if (choice.kind === 'hwv2' && choice.radio) {
        if (!choice.radio.checked) {
          choice.radio.checked = true;
          choice.radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.addVariantId(parseInt(choice.value, 10));
        return;
      }

      if (this.select && choice.value != null) {
        this.select.value = choice.value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.handleAdd(true);
    }

    /* ---------------- Add to cart ---------------- */
    async handleAdd(fromSheet) {
      if (!fromSheet && this.shouldOpenSheet()) {
        this.openSheet();
        return;
      }

      if (this.bundle) {
        this.handleBundleAdd();
        return;
      }

      const opt = this.selectedOption();
      let variantId;
      if (this.select) {
        if (!opt || opt.dataset.available === 'false') return;
        variantId = parseInt(this.select.value, 10);
      } else {
        // Single-variant product: id baked onto the element by Liquid fallback.
        variantId = parseInt(this.dataset.variantId, 10);
      }
      if (!variantId) {
        window.location.href = '/products/' + (this.dataset.handle || '');
        return;
      }

      this.addVariantId(variantId);
    }

    async addVariantId(variantId) {
      if (!variantId) return;
      this.setLoading(true);

      const body = { items: [{ id: variantId, quantity: 1 }] };
      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        body.sections = this.cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
        if (typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(document.activeElement);
        }
      }

      try {
        const response = await fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          this.setLoading(false);
          this.onVariantChange();
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sticky-atc', productVariantId: variantId });
        }

        if (this.cart && typeof this.cart.renderContents === 'function') {
          if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          this.cart.renderContents(data);
        } else {
          window.location.href = this.cartUrl;
        }
      } catch (e) {
        window.location.href = this.cartUrl;
      } finally {
        this.setLoading(false);
        this.onVariantChange();
      }
    }

    handleBundleAdd() {
      const sel = this.selectedBundleOption();
      if (!sel || sel.soldout) return;
      this.syncBundleSelection();
      const submit = this.bundle.querySelector('[data-bundle-submit]');
      // Defer a tick so the radio change settles before the builder reads it.
      if (submit) setTimeout(() => submit.click(), 0);

      const original = this.defaultLabel;
      this.setButton(true, 'Added!');
      this.button.disabled = true;
      setTimeout(() => {
        this.button.disabled = false;
        this.updateBundleState();
        this.defaultLabel = original;
      }, 2000);
    }

    /* ---------------- Sticky → page variant sync ----------------
       Drive Dawn's <variant-selects>/<variant-radios> from the sticky dropdown
       by setting the matching option inputs and firing one change event. Dawn
       then updates the price, media gallery, URL and the form's variant id, so
       the page's own Add to Cart and the sticky one stay consistent. */
    syncVariantToPage() {
      if (this.bundle || !this.select) return;
      const opt = this.selectedOption();
      if (!opt) return;
      const picker = document.querySelector('variant-selects, variant-radios');
      if (!picker) return;

      const values = [opt.dataset.option1, opt.dataset.option2, opt.dataset.option3];
      const esc = (v) => (window.CSS && CSS.escape ? CSS.escape(v) : v);

      picker.querySelectorAll('select').forEach((sel, i) => {
        if (values[i] != null && values[i] !== '') sel.value = values[i];
      });
      picker.querySelectorAll('fieldset.product-form__input').forEach((fs, i) => {
        if (values[i] == null || values[i] === '') return;
        const radio = fs.querySelector('input[type="radio"][value="' + esc(values[i]) + '"]');
        if (radio) radio.checked = true;
      });

      // The listener lives on the picker element itself.
      picker.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* ---------------- Page → sticky variant sync ---------------- */
    subscribeVariantChange() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      if (!PUB_SUB_EVENTS.variantChange) return;
      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data && event.data.variant;
        if (!variant || !this.select || this.bundle) return;
        const match = Array.prototype.slice
          .call(this.select.options)
          .filter((o) => parseInt(o.value, 10) === Number(variant.id))[0];
        if (match) {
          this.select.value = match.value;
          if (variant.featured_image && variant.featured_image.src) {
            match.dataset.image = variant.featured_image.src;
            delete match.dataset.imageSrcset;
          }
          this.onVariantChange();
        }
      });
    }
  }

  customElements.define('sticky-atc', StickyAtc);
})();
